// format.js — domain-specific formatters for events, leases, chats,
// agents, repos, resources.
//
// All exports are pure functions: they take their inputs as
// arguments and return strings or plain objects. No DOM mutation,
// no state reads, no fetches. That's the contract for code that
// lives here.
//
// Imports from src/util.js (escapeHTML, ago, shortTime) for
// formatting; imports from src/dom.js are NOT allowed (DOM-touching
// code is per-page, not per-formatter).

import { escapeHTML, ago, shortTime } from "./util.js";
import { state } from "./state.js";

// ---- event drill-in (expand state) ----
// Set of event ids whose row is expanded to show the full writeup. Lives here
// (not in state.js) so eventRowHTML can read it without an import cycle —
// format.js is a leaf that state.js-consuming pages import. bootstrap.js toggles
// membership via the exported helpers and re-renders the active page.
const expandedEventIds = new Set();
export function eventIsExpanded(eid) {
  return eid != null && expandedEventIds.has(String(eid));
}
export function toggleEventExpanded(eid) {
  if (eid == null) return;
  const k = String(eid);
  if (expandedEventIds.has(k)) expandedEventIds.delete(k);
  else expandedEventIds.add(k);
}

// Cache of FULL events fetched by id (GET /v1/events/by-id) — keyed by the
// numeric id (the "c" cloud prefix stripped). The list rows that feed the
// dashboard come through the teams/cloud mapper which strips the rich payload
// (only payload_hint survives, empty without an hmac key), so on expand we fetch
// the authoritative full event and render THAT instead of the thin row.
const fullEventCache = new Map();
// numericEventId returns the integer row id behind a coordination event
// id, or "" when there is no fetchable by-id source. Event ids are minted
// client-side with a type prefix: coordination events → "c"+id
// (decrypt-helpers.js), audit events → "a"+id (bootstrap.js). ONLY
// coordination events live in the integer-keyed `events` table that
// GET /v1/events/by-id/{int} reads; audit events come from audit_log and
// have no by-id source.
//
// So: strip a leading "c" only, then require the remainder be all digits.
//   c297701 → 297701    (fetchable)
//   a217    → "a217" → not all digits → ""   (audit; no source)
//   297701  → 297701    (bare numeric, also fetchable)
//   anything else → ""
// Crucially we do NOT strip "a": stripping it would leave a numeric
// "217" that looks fetchable but points at a different table's row — the
// "a217 → 400" bug. Returning "" makes callers skip the fetch entirely.
export function numericEventId(eid) {
  const s = String(eid == null ? "" : eid).replace(/^c/, "");
  return /^\d+$/.test(s) ? s : "";
}
export function cacheFullEvent(ev) {
  const id = numericEventId(ev && (ev.id ?? ev.EventID ?? ev.event_id));
  if (id) fullEventCache.set(id, ev);
}
export function hasFullEvent(eid) {
  return fullEventCache.has(numericEventId(eid));
}

// Payload keys that are noise in a human writeup (hashes, internal chain
// fields) — skipped in the expanded detail.
const EVENT_DETAIL_SKIP = new Set([
  "row_hash",
  "prev_hash",
  "signing_key_id",
  "warning", // already surfaced as the stale-warning pill
]);

// eventDetailHTML renders the full event as labeled key/value lines: the
// first-class columns plus EVERY payload key (the richest part, hidden behind a
// hover tooltip today). Reuses the collision-detail .spend-ix-* styling.
export function eventDetailHTML(e) {
  // Prefer the authoritative full event (fetched by id on expand) over the thin
  // list row, which may have come through the cloud mapper with the payload
  // stripped. Falls back to the row itself if no fetch happened.
  const rawId = e.EventID ?? e.event_id ?? e.id;
  const full = fullEventCache.get(numericEventId(rawId));
  if (full) e = full;
  const payload = e.Payload || e.payload || {};
  const rows = [];
  // copyLines accumulates "Label: value" for the whole-event "Copy event" button.
  const copyLines = [];
  const add = (label, val) => {
    if (val === undefined || val === null || val === "") return;
    let s = typeof val === "object" ? JSON.stringify(val) : String(val);
    copyLines.push(label + ": " + s);
    const display = s.length > 300 ? s.slice(0, 297) + "…" : s;
    // Each row is click-to-copy its FULL value (the untruncated `s`). data-copy-kv
    // carries the value; the delegated handler flashes a "copied" state via a CSS
    // class (no markup-destroying textContent swap). title hints the interaction.
    rows.push(
      `<div class="spend-ix-kv kv-copyable" data-copy-kv="${escapeHTML(s)}" title="Click to copy"><span class="spend-ix-k">${escapeHTML(label)}</span><span class="spend-ix-v">${escapeHTML(display)}</span><span class="kv-copied-flag">copied</span></div>`,
    );
  };
  const eid = e.EventID || e.event_id || e.id || "";
  add("Event", eventTypeOf(e));
  const ts = e.OccurredAt || e.occurred_at || "";
  if (ts) {
    let abs = ts;
    try {
      abs = new Date(ts).toLocaleString();
    } catch {
      /* keep raw */
    }
    add("When", abs);
  }
  add("Agent", e.AgentID || e.agent_id);
  add("Resource", e.Resource || e.resource);
  add("Outcome", e.Outcome || e.outcome);
  add("Intent", e.Intent || e.intent);
  add("Tool", e.Tool || e.tool);
  add("Lease", e.LeaseID || e.lease_id || payload.lease_id);
  add("Worktree", e.WorktreeID || e.worktree_id);
  const ft = e.FencingToken ?? e.fencing_token;
  if (ft !== undefined && ft !== null) add("Fencing token", ft);
  // Every remaining payload key (the part the row hides). Sorted for stability.
  for (const k of Object.keys(payload).sort()) {
    if (EVENT_DETAIL_SKIP.has(k)) continue;
    add(k, payload[k]);
  }
  // Lead with a plain-English explanation of WHAT happened and WHY it matters,
  // so the expansion isn't just a key/value dump. The group gives a colored
  // header; the narrative weaves in the cross-agent detail (who held it / who
  // was blocked / which branch) when present, falling back to the static tip.
  const grp = eventGroup(eventTypeOf(e), payload);
  const meta = friendlyEvent(eventTypeOf(e), payload);
  const narrative = eventNarrative(e, payload, meta);
  // "Copy event" copies the whole event as a plain "Label: value\n…" block — the
  // same lines the detail rows show. data-copy-event carries the text; the
  // delegated handler flashes the button. Title-cased label + a unicode clipboard
  // glyph keep it unobtrusive but discoverable.
  const copyAll = escapeHTML(copyLines.join("\n"));
  const header = `<div class="event-expand-head">
      <span class="ev-dot" style="--ev: ${grp.color};"></span>
      <span class="event-expand-title">${escapeHTML(meta.label)}</span>
      <span class="event-expand-group" style="color: ${grp.color};">${escapeHTML(grp.label)}</span>
      <button type="button" class="event-copy-btn" data-copy-event="${copyAll}" title="Copy this event">⧉ Copy event</button>
    </div>
    <div class="event-expand-narrative">${narrative}</div>`;
  return `<div class="event-expand">${header}<div class="event-expand-kv">${rows.join("")}</div></div>`;
}

// eventNarrative builds the human "here's what happened" sentence shown atop the
// expansion. It uses the cross-agent payload detail when present (which branch,
// who held the file, who got preempted), so a flagged event reads as a story —
// "two chats were on branch X" rather than a bare type. Falls back to the static
// EVENT_META tip. All values escaped; returns a safe HTML string.
function eventNarrative(e, payload, meta) {
  const esc = (v) => escapeHTML(String(v || ""));
  const t = e.EventType || e.event_type || "";
  const agent = e.AgentID || e.agent_id || "";
  const short = (a) => {
    if (!a) return "";
    const p = String(a).split("/");
    return p[0] + (p[1] ? " · " + p[1].slice(0, 8) : "");
  };
  const branch = payload.branch || payload.Branch || "";
  const heldBy = payload.held_by_agent || payload.held_by || payload.preempted_agent || "";
  const reqBy = payload.requested_by || payload.by_agent || agent || "";
  if (t === "branch.contended" && branch) {
    return `Two chats were working the same git branch <code>${esc(branch)}</code> at once${
      reqBy ? ` — flagged when <strong>${esc(short(reqBy))}</strong> touched it` : ""
    }. AgentCollision surfaces this so the chats coordinate the branch instead of silently diverging.`;
  }
  if ((t === "lease.expired" && payload.reason === "heartbeat_stale")) {
    return `<strong>${esc(short(agent))}</strong> stopped sending heartbeats while holding this file — the chat likely crashed or was closed mid-edit. The lease was reclaimed so others aren't blocked, but its last edits may be uncommitted (check git status).`;
  }
  if (t === "lease.preempted") {
    return `A higher-priority chat forced <strong>${esc(short(heldBy || agent))}</strong> off this resource${
      reqBy && reqBy !== (heldBy || agent) ? ` in favor of <strong>${esc(short(reqBy))}</strong>` : ""
    }. The preempted work may have been interrupted mid-edit.`;
  }
  if (t === "lease.blocked") {
    return `<strong>${esc(short(reqBy))}</strong> tried to edit a file another chat is actively holding — AgentCollision blocked the overwrite so the two don't clash.`;
  }
  if (isOldUnheldChange(t, payload)) {
    return esc(meta.tip);
  }
  if (t === "write.unmanaged_conflict" || t === "delete.unmanaged_conflict") {
    const verb = t.startsWith("delete") ? "deleted" : "wrote to";
    return `Something ${verb} a held file from OUTSIDE coordination (a manual edit, another tool, or an un-hooked agent). AgentCollision caught it and recorded this audit event — the change wasn't coordinated, so review it.`;
  }
  if (t === "commit.bypass_detected") {
    return `A commit touched a file a peer was holding a lease on, outside coordination (e.g. <code>git commit --no-verify</code>). Flagged so you can check the commit didn't clobber in-flight work.`;
  }
  if (t.startsWith("lease.negotiation_")) {
    return `Part of the autonomous handoff protocol: ${esc(meta.tip)} No human was needed — the chats brokered the resource between themselves.`;
  }
  // Generic fallback: the static tip is still a real explanation.
  return esc(meta.tip);
}

function eventTypeOf(e) {
  return e.EventType || e.event_type || "";
}

// ---- agent / tool ----

// parseAgentID splits "claude-code/<uuid>" → { tool, shortID }.
// shortID is the first 8 chars of the uuid (or of the whole agentID
// if there's no slash). Unknown input returns {tool: 'unknown',
// shortID: '???'}.
export function parseAgentID(agentID) {
  if (!agentID) return { tool: "unknown", shortID: "???" };
  const parts = agentID.split("/");
  const tool = parts[0] || "unknown";
  const uuid = parts.slice(1).join("/");
  const shortID = uuid ? uuid.slice(0, 8) : agentID.slice(0, 8);
  return { tool, shortID };
}

// chatLabel names a chat for people: "<runtime> <short id>" for a session
// ("claude-code/<uuid>"), and AgentCollision itself for its own notices
// (ids with no runtime prefix like "ac-daemon", which used to read
// "ac-daemon ac-daemo").
export function chatLabel(agentID) {
  if (!agentID) return "—";
  if (isAgentCollisionID(agentID)) return "AgentCollision";
  if (!agentID.includes("/")) return agentID;
  const { tool, shortID } = parseAgentID(agentID);
  return tool + " " + shortID;
}

// isAgentCollisionID: AgentCollision's own ids — "agentcollision/daemon",
// "ac-daemon", "ac:discovered" — as opposed to a chat's.
export function isAgentCollisionID(agentID) {
  const runtime = String(agentID || "").split("/")[0].toLowerCase();
  return runtime === "agentcollision" || runtime === "ac-daemon" || runtime.startsWith("ac:");
}

// toolIcon returns the inline SVG for a tool runtime. Falls back
// to a generic "?" icon for unknown tools.
export function toolIcon(tool) {
  const s =
    'width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -2px;"';
  switch (tool) {
    case "claude-code":
      return `<svg ${s}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>`;
    case "cursor":
      return `<svg ${s}><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"/><path d="M12 12v9"/><path d="m8 17 4 4 4-4"/></svg>`;
    case "windsurf":
      return `<svg ${s}><path d="M2 12S6.5 4 12 4s10 8 10 8-4.5 8-10 8S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>`;
    case "cline":
      return `<svg ${s}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`;
    default:
      return `<svg ${s}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;
  }
}

// ---- purpose / worktree / repo ----

// purposeToPath returns the file path a lease purpose names, or "". Only the
// shapes the daemon writes around a path count:
//   "Editing <path>" / "Reading <path>", optionally followed by
//   " (after <Tool>)" or " — <declared intent>"
//   "auto-acquired via PreToolUse on <path>" (daemons before v0.40)
// A command purpose ("Running: <command>") names no file. The old /on (.+)$/
// matched "on " inside any word, so "Running: Mutation check: test fails
// without the GUC" became the "path" "check: test fails without the GUC".
const PURPOSE_PATH = /^(?:Editing|Reading) (.+?)(?: \(after [^)]*\))?(?: — .*)?$/;
const LEGACY_PURPOSE_PATH = /^auto-acquired via \S+ on (.+)$/;
export function purposeToPath(purpose) {
  if (!purpose) return "";
  const m = PURPOSE_PATH.exec(purpose) || LEGACY_PURPOSE_PATH.exec(purpose);
  return m ? m[1] : "";
}

// purposeNote is what a purpose says beyond the file it names: the whole
// purpose when it names none ("Running: npm test"), the declared intent after
// " — " when it does, and "" for a bare "Editing <path>" or a placeholder.
export function purposeNote(purpose) {
  if (!purpose || isPlaceholderPurpose(purpose)) return "";
  if (!purposeToPath(purpose)) return purpose;
  const i = purpose.indexOf(" — ");
  return i >= 0 ? purpose.slice(i + 3) : "";
}

// isPlaceholderPurpose: text the daemon wrote where nobody stated a purpose.
// Records from before v0.76.4 still carry these.
export function isPlaceholderPurpose(purpose) {
  return /^auto-(inferred|acquired)\b/i.test(String(purpose || "").trim());
}

// worktreeFromPurpose pulls the worktree id out of a purpose string
// that embeds "/oj-worktrees/<id>". Falls back to "".
export function worktreeFromPurpose(purpose) {
  if (!purpose) return "";
  const m = purpose.match(/\/oj-worktrees\/([^/]+)/);
  return m ? m[1] : "";
}

// repoFromResource pulls the repo name out of a lease/event Resource.
// The wire format is "file:<repo-root>:<rel-path>" — e.g.
//   file:/Users/vsgro/Desktop/OuterJoynMain:src/foo.ts
// We return the basename of <repo-root> ("OuterJoynMain") so chat
// cards, lease rows, and the activity feed can show "which repo is
// this row for" without needing to fetch /v1/worktrees and join.
// Returns '' for hashed-only resources, infra/subsystem resources,
// or anything that doesn't match the file:<root>:<rel> shape.
export function repoFromResource(resource) {
  if (!resource || typeof resource !== "string") return "";
  if (!resource.startsWith("file:")) return "";
  if (looksLikeHashedResource(resource)) return "";
  // file:/abs/path/to/repo:rel/path → ['file', '/abs/path/to/repo', 'rel/path']
  const parts = resource.split(":");
  if (parts.length < 3) return "";
  const root = parts[1];
  if (!root) return "";
  const last = root.replace(/\/+$/, "").split("/").pop();
  return last || "";
}

// repoFromPurpose handles auto-acquired purposes that embed a path
// in a sibling worktree dir. Format:
//   "auto-acquired via PreToolUse on /Users/vsgro/Desktop/oj-worktrees/<id>/<rest>"
// The leading slash-prefixed dir before the worktree segment is the
// canonical repo. Falls back to "" when no recognisable shape.
//
// Special case: oj-worktrees → OuterJoynMain, ac-worktrees →
// agentcollision (those are the canonical worktree pool names).
export function repoFromPurpose(purpose) {
  if (!purpose || typeof purpose !== "string") return "";
  const m = purpose.match(/\son\s+(\/[^\s]+)/);
  if (!m) return "";
  const abs = m[1];
  const seg = abs.match(/\/Desktop\/([^/]+)/);
  if (!seg) return "";
  let name = seg[1];
  if (name === "oj-worktrees") return "OuterJoynMain";
  if (name === "ac-worktrees") return "agentcollision";
  return name;
}

// repoFor returns the best repo guess for a row. Preference order:
//   1. An explicit decrypted repo label (the lease's repo_hint, daemon
//      v0.45+) — the only source that survives the daemon stripping the
//      repo root out of display_hint. "owner/repo" or a basename.
//   2. The Resource (structured, but repo-relative on team tier so it
//      only yields a repo on local/solo rows where the root is intact).
//   3. The free-form Purpose.
// The explicit label is optional so existing call sites that pass only
// (resource, purpose) keep working; pre-v0.45 rows fall through to the
// Resource/Purpose heuristics exactly as before.
export function repoFor(resource, purpose, repoLabel) {
  return (
    (repoLabel || "").trim() ||
    repoFromResource(resource) ||
    repoFromPurpose(purpose) ||
    ""
  );
}

// ---- resource / path ----

// Worktree prefixes the resource's relative path may start with.
// Stripped before display so users see "backend/src/foo.ts" rather
// than "ac-worktrees/wt_2026-04-20-b6be1a08/backend/src/foo.ts".
const WORKTREE_PREFIX_PATTERNS = [
  /^ac-worktrees\/[^/]+\//,
  /^oj-worktrees\/[^/]+\//,
  /^worktrees?\/[^/]+\//,
];

export function stripWorktreePrefix(path) {
  if (!path) return path;
  for (const p of WORKTREE_PREFIX_PATTERNS) {
    if (p.test(path)) return path.replace(p, "");
  }
  return path;
}

// looksLikeHashedResource matches the wire form the coordinator
// stores when it's never been augmented with a display_hint, e.g.
// "file:h:0123abcd…:h:0123abcd…" or the current "filev2:h:…:h:…"
// (HMAC of the worktree + HMAC of the relative path). Decryption either failed or never happened.
// Callers use this to bail out of rendering instead of showing
// raw hex.
export function looksLikeHashedResource(resource) {
  return /^file(v2)?:h:[0-9a-f]+(:h:[0-9a-f]+)+$/i.test(resource);
}

// isInfraResource returns true for "infra:..." resources, which
// represent server/database/queue resources rather than files.
export function isInfraResource(resource) {
  return resource && resource.startsWith("infra:");
}

// friendlyResource converts a wire Resource string to a display
// path:
//   "infra:prod-server"           → "prod-server"
//   "file:/Users/foo/repo:src/x"  → "src/x"
//   "subsystem:auth"              → "auth"
// Hashed-only file resources return "" so callers fall through to
// "Browsing" instead of showing hex.
export function friendlyResource(resource) {
  if (!resource) return "";
  if (resource.startsWith("infra:")) return resource.slice(6);
  if (resource.startsWith("file:")) {
    if (looksLikeHashedResource(resource)) return "";
    const lastColon = resource.lastIndexOf(":");
    const tail = lastColon > 5 ? resource.slice(lastColon + 1) : resource.slice(5);
    return stripWorktreePrefix(tail);
  }
  if (resource.startsWith("subsystem:")) return resource.slice(10);
  return resource;
}

// resourceTypeIcon returns the small SVG icon for a lease/event row:
// a server icon for infra:, a file icon otherwise.
export function resourceTypeIcon(resource) {
  const s =
    'width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -2px; opacity: 0.7;"';
  if (isInfraResource(resource)) {
    return `<svg ${s}><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>`;
  }
  return `<svg ${s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;
}

// shortPath strips common absolute-path prefixes so a long
// /Users/.../Desktop/repo/src/foo.ts renders as "src/foo.ts".
// Handles worktrees + Desktop repos + generic /Users/<name>/.
export function shortPath(path) {
  if (!path) return "";
  const patterns = [
    /^.*\/ac-worktrees\/[^/]+\//, // ac-worktrees/<id>/
    /^.*\/oj-worktrees\/[^/]+\//, // oj-worktrees/<id>/
    /^.*\/worktrees?\/[^/]+\//, // generic worktrees/<id>/ or worktree/<id>/
    /^.*\/Desktop\/[^/]+\//, // ~/Desktop/RepoName/
    /^\/Users\/[^/]+\//, // /Users/name/ → relative
  ];
  for (const p of patterns) {
    if (p.test(path)) return path.replace(p, "");
  }
  return path;
}

// ---- lease state ----

// leaseStateIcon returns an inline SVG for a lease state cell.
// Colors: held=green, lock_delay=yellow, released=gray,
// expired=orange, revoked=red, unknown=light-gray.
export function leaseStateIcon(state) {
  const s =
    'width="14" height="14" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
  switch (state) {
    case "held":
      return `<svg ${s} stroke="#22c55e"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
    case "lock_delay":
      return `<svg ${s} stroke="#eab308"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;
    case "released":
      return `<svg ${s} stroke="#6b7280"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>`;
    case "expired":
      return `<svg ${s} stroke="#f97316"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;
    case "revoked":
      return `<svg ${s} stroke="#ef4444"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;
    default:
      return `<svg ${s} stroke="#9ca3af"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;
  }
}

// leaseStateTip returns the hover tooltip for a lease state cell.
export function leaseStateTip(state) {
  switch (state) {
    case "held":
      return "Held — an agent is actively editing this file";
    case "lock_delay":
      return "Cooldown — just released, brief wait before another agent can acquire";
    case "released":
      return "Released — agent finished editing, file is free";
    case "expired":
      return "Expired — lease timed out (agent stopped heartbeating)";
    case "revoked":
      return "Revoked — force-released by operator or system restart";
    default:
      return state;
  }
}

// ---- event metadata ----

const EVENT_META = {
  "lease.acquired": { icon: "&#9654;", label: "Chat started editing", cls: "tb-acquired", tip: "A chat claimed exclusive write access to this file." },
  "lease.released": { icon: "&#10003;", label: "Chat finished cleanly", cls: "tb-released", tip: "The chat finished its edit and voluntarily released the lease." },
  "lease.expired": { icon: "&#9201;", label: "Lease timed out", cls: "tb-expired", tip: "The lease hit its TTL without being explicitly released." },
  "lease.cooldown_elapsed": { icon: "&#9679;", label: "Ready for next chat", cls: "tb-cooldown_elapsed", tip: "The brief post-release cooldown window ended." },
  "lease.heartbeat": { icon: "&#10227;", label: "Lease renewed", cls: "tb-heartbeat", tip: "The holder sent a heartbeat, extending its lease." },
  "lease.blocked": { icon: "&#8856;", label: "Blocked -- held by another", cls: "tb-blocked", tip: "A chat tried to edit a file that is currently held by a different chat." },
  "lease.preempted": { icon: "&#8645;", label: "Preempted -- higher priority", cls: "tb-expired", tip: "A higher-priority chat forced a lower-priority holder off this resource." },
  "lease.revoked": { icon: "&#10007;", label: "Force released", cls: "tb-blocked", tip: "The lease was force-released by an operator or system restart. The previous holder may have been mid-edit." },
  "message.sent": { icon: "&#9993;", label: "Direct message sent", cls: "tb-heartbeat", tip: "One chat sent a direct message to another." },
  "message.broadcast": { icon: "&#128227;", label: "Broadcast to team", cls: "tb-heartbeat", tip: "A chat broadcast a message to every reachable peer." },
  "message.replied": { icon: "&#8617;", label: "Reply sent", cls: "tb-released", tip: "A chat replied to a teammate's message (the ack norm at work)." },
  "note.added": { icon: "&#128204;", label: "Bulletin note pinned", cls: "tb-released", tip: "A durable fact was pinned to the team bulletin (agc note)." },
  "write.unmanaged_conflict": { icon: "&#9888;", label: "Unmanaged write caught", cls: "tb-blocked", tip: "Something outside AgentCollision wrote to a file another chat held, or to a file in a folder several chats share." },
  "delete.unmanaged_conflict": { icon: "&#9888;", label: "Unmanaged delete caught", cls: "tb-blocked", tip: "Something outside AgentCollision deleted a file another chat held, or a file in a folder several chats share." },
  "commit.bypass_detected": { icon: "&#9888;", label: "Commit bypass caught", cls: "tb-expired", tip: "A commit touched a file a peer was holding, outside coordination." },
  "branch.contended": { icon: "&#9094;", label: "Branch contention flagged", cls: "tb-expired", tip: "Two agents were working the same git branch -- flagged to coordinate." },
  // Negotiation protocol — a blocked agent and the holder broker a handoff.
  "lease.negotiation_opened":    { icon: "&#9993;", label: "Negotiation opened",       cls: "tb-cooldown_elapsed", tip: "A blocked agent opened a negotiation with the holder." },
  "lease.negotiation_granted":   { icon: "&#10003;", label: "Coordinated autonomously", cls: "tb-acquired", tip: "An idle holder's file was handed off to a waiting agent -- no human needed." },
  "lease.negotiation_countered": { icon: "&#8645;", label: "Negotiation countered",     cls: "tb-heartbeat", tip: "The holder countered the waiting agent's request." },
  "lease.negotiation_accepted":  { icon: "&#10003;", label: "Negotiation accepted",      cls: "tb-acquired", tip: "The negotiation was accepted and the lease handed off." },
  "lease.negotiation_resolved":  { icon: "&#10003;", label: "Negotiation resolved",      cls: "tb-released", tip: "The negotiation concluded." },
  "lease.revoke_requested":      { icon: "&#9888;", label: "Revoke requested",           cls: "tb-expired", tip: "An agent requested that a held lease be revoked." },
  // Audit events (from coordinator audit_log). Keys match the `action`
  // column directly — the dashboard no longer double-prefixes with
  // "team." so the keys here match the wire format verbatim.
  "team.created":            { icon: "&#9733;", label: "Team created",      cls: "tb-acquired", tip: "A new team was created." },
  "team.renamed":            { icon: "&#9998;", label: "Team renamed",      cls: "tb-released", tip: "The team was renamed." },
  "team.deleted":            { icon: "&#10007;", label: "Team deleted",     cls: "tb-blocked",  tip: "The team was permanently deleted." },
  // Legacy events — the Owner role was retired in migration 035. These
  // mappings remain so historical audit rows render with a label/icon
  // instead of falling through to the generic renderer.
  "team.owner_backfilled":   { icon: "&#9881;", label: "Owner backfilled",  cls: "tb-heartbeat", tip: "Legacy event: oldest admin was promoted to owner. The Owner role was retired in migration 035; this event no longer fires." },
  "team.ownership_transferred": { icon: "&#8645;", label: "Ownership transferred", cls: "tb-heartbeat", tip: "Legacy event: ownership was transferred between admins. The Owner role was retired in migration 035; this event no longer fires." },
  "team.switched":           { icon: "&#8645;", label: "Team switched",     cls: "tb-heartbeat", tip: "A laptop was bound to a different team." },
  "member.joined":           { icon: "&#43;",   label: "Member joined",     cls: "tb-acquired", tip: "A new member joined the team." },
  "member.removed":          { icon: "&#8722;", label: "Member removed",    cls: "tb-blocked",  tip: "A member was removed from the team." },
  "member.role_changed":     { icon: "&#8645;", label: "Role changed",      cls: "tb-heartbeat", tip: "A member's role was changed." },
  "member.renamed":          { icon: "&#9998;", label: "Member renamed",    cls: "tb-released", tip: "A member changed their display name." },
  "invite.created":          { icon: "&#9993;", label: "Invite created",    cls: "tb-heartbeat", tip: "A new invite code was generated." },
  "invitation.created":      { icon: "&#9993;", label: "Invitation sent",   cls: "tb-heartbeat", tip: "An email invitation was sent." },
  "invitation.accepted":     { icon: "&#10003;", label: "Invitation accepted", cls: "tb-acquired", tip: "An invited user joined the team." },
  "invites.revoked":         { icon: "&#10007;", label: "Invites revoked",   cls: "tb-expired",  tip: "All pending invite codes were revoked." },
  "user.linked":             { icon: "&#9741;", label: "User linked",        cls: "tb-heartbeat", tip: "A dashboard user was linked to this team via invite code." },
  "device_auth.approved":    { icon: "&#9989;", label: "Device authorized",  cls: "tb-acquired", tip: "A CLI device-auth flow was approved." },
  "install-token.created":   { icon: "&#9993;", label: "Install token issued", cls: "tb-heartbeat", tip: "A one-shot install token was minted." },
  "rule.appended":           { icon: "&#9999;", label: "Rule added",         cls: "tb-heartbeat", tip: "A policy or priority rule was appended." },
  "settings.updated":        { icon: "&#9881;", label: "Settings updated",   cls: "tb-heartbeat", tip: "Team settings were changed." },
  "billing.checkout_started":{ icon: "&#9733;", label: "Checkout started",   cls: "tb-heartbeat", tip: "A user initiated a Stripe checkout." },
  "retention.pruned":        { icon: "&#9851;", label: "Retention sweep",    cls: "tb-released", tip: "Old data was pruned per the team's retention policy." },
  // Coordination events beyond the core lease lifecycle.
  "lease.nudged":                { icon: "&#9201;", label: "Quiet holder nudged",        cls: "tb-expired", tip: "The chat holding this file had gone quiet, so it was nudged to finish or let go." },
  "lease.yield_requested":       { icon: "&#8645;", label: "Asked holder to hand over",  cls: "tb-heartbeat", tip: "A waiting chat asked the holder to hand this file over." },
  "lease.yield_accepted":        { icon: "&#10003;", label: "Holder handed over",        cls: "tb-acquired", tip: "The holder agreed and handed the file to the waiting chat." },
  "lease.yield_refused":         { icon: "&#10007;", label: "Holder kept the file",      cls: "tb-expired", tip: "The holder was mid-edit and declined to hand the file over yet." },
  "lease.stale_holder_skipped":  { icon: "&#9201;", label: "Silent holder skipped",      cls: "tb-expired", tip: "The chat holding this file had stopped responding, so it no longer blocked others." },
  "lease.suspicious_split":      { icon: "&#9888;", label: "Split edit flagged",         cls: "tb-expired", tip: "One change looked split across chats to get around a held file." },
  "lease.cross_repo_mismatch":   { icon: "&#9888;", label: "Wrong-repo lease ignored",   cls: "tb-expired", tip: "A lease from a different repo matched this file by mistake and was ignored, so it blocked nothing." },
  "lease.xnegotiation_received": { icon: "&#9993;", label: "Handoff asked (other machine)", cls: "tb-cooldown_elapsed", tip: "A chat on another machine asked for a file held here." },
  "lease.xnegotiation_granted":  { icon: "&#10003;", label: "Handoff granted (other machine)", cls: "tb-acquired", tip: "A file was handed to a chat on another machine." },
  "lease.xnegotiation_countered":{ icon: "&#8645;", label: "Handoff countered (other machine)", cls: "tb-heartbeat", tip: "The holder answered a request from another machine with a counter-offer." },
  "lease.xnegotiation_resolved": { icon: "&#10003;", label: "Handoff settled (other machine)", cls: "tb-released", tip: "A handoff with a chat on another machine concluded." },
  "write.unmanaged_acknowledged":{ icon: "&#10003;", label: "Unmanaged write accepted",   cls: "tb-released", tip: "Someone reviewed a write made outside coordination and accepted it." },
  "write.recorded":              { icon: "&#9998;", label: "Edit recorded",              cls: "tb-released", tip: "A chat's edit to a file it held was recorded." },
  "write.stale_rejected":        { icon: "&#10007;", label: "Out-of-date edit refused",  cls: "tb-blocked", tip: "A chat tried to write with a lease that had already been replaced, so the edit was refused." },
  "write.unmanaged_cascade":     { icon: "&#9888;", label: "Many files changed at once", cls: "tb-blocked", tip: "Lots of files changed together outside coordination (for example git checking out a branch or creating a worktree). The first few are listed one by one; the rest are counted in this one event." },
  "delete.unmanaged_cascade":    { icon: "&#9888;", label: "Many files deleted at once", cls: "tb-blocked", tip: "A whole folder was removed outside coordination (for example a worktree). Shown as one event instead of one per file." },
  "commit.bypass_blocked":       { icon: "&#8856;", label: "Commit bypass stopped",      cls: "tb-blocked", tip: "A commit would have touched a file a peer was holding, so it was stopped." },
  "policy.fired":                { icon: "&#9873;", label: "Rule triggered",             cls: "tb-expired", tip: "One of your team's rules matched this action." },
  "decision.captured":           { icon: "&#128204;", label: "Decision recorded",        cls: "tb-released", tip: "A chat recorded a decision it made." },
  "agent.registered":            { icon: "&#43;",   label: "Chat started",               cls: "tb-acquired", tip: "A new AI chat session checked in." },
  "agent.connected":             { icon: "&#9679;", label: "Chat connected",             cls: "tb-acquired", tip: "A chat session connected." },
  "agent.disconnected":          { icon: "&#9675;", label: "Chat disconnected",          cls: "tb-released", tip: "A chat session disconnected." },
  "agent.deregistered":          { icon: "&#8722;", label: "Chat removed",               cls: "tb-released", tip: "A chat session's record was removed." },
  "agent.spend_threshold_crossed": { icon: "&#36;", label: "Spend limit reached",        cls: "tb-expired", tip: "A chat's AI spend passed the threshold you set." },
  "worktree.allocated":          { icon: "&#43;",   label: "Worktree created",           cls: "tb-acquired", tip: "A separate working copy of the repo was set up for a chat." },
  "worktree.released":           { icon: "&#10003;", label: "Worktree released",         cls: "tb-released", tip: "A chat finished with its separate working copy." },
  "worktree.removed":            { icon: "&#8722;", label: "Worktree removed",           cls: "tb-released", tip: "A separate working copy was deleted." },
  "worktree.gc_completed":       { icon: "&#9851;", label: "Old worktrees cleaned up",   cls: "tb-released", tip: "Working copies nobody was using were cleaned up." },
  "message.delivered":           { icon: "&#9993;", label: "Message delivered",          cls: "tb-heartbeat", tip: "A message reached the chat it was sent to." },
  "message.read":                { icon: "&#10003;", label: "Message read",              cls: "tb-released", tip: "A chat read a message sent to it." },
  "message.policy_denied":       { icon: "&#8856;", label: "Message blocked by rule",    cls: "tb-blocked", tip: "A message was not sent because one of your rules forbids it." },
  "message.rate_limited":        { icon: "&#9201;", label: "Message held back",          cls: "tb-expired", tip: "A chat sent too many messages too fast, so this one was held back." },
  "message.pushed":              { icon: "&#9993;", label: "Message typed into chat",    cls: "tb-heartbeat", tip: "A message was typed straight into a waiting chat." },
  "message.push_confirmed":      { icon: "&#10003;", label: "Message arrival confirmed", cls: "tb-released", tip: "The chat confirmed a typed-in message arrived." },
  "message.doorbell_rung":       { icon: "&#128276;", label: "Idle chat woken",          cls: "tb-heartbeat", tip: "An idle chat was woken so it would read a new message." },
  "message.doorbell_suggested":  { icon: "&#128276;", label: "Wake-up suggested",        cls: "tb-heartbeat", tip: "A chat was told how to wake a peer that had a message waiting." },
  "message.reply_nudged":        { icon: "&#8617;", label: "Reply reminder",             cls: "tb-heartbeat", tip: "A chat was reminded to answer a message." },
  "message.delivery_retried":    { icon: "&#10227;", label: "Delivery retried",          cls: "tb-heartbeat", tip: "A message that did not arrive the first time was sent again." },
  "message.federated":           { icon: "&#9993;", label: "Message sent to another machine", cls: "tb-heartbeat", tip: "A message went to a chat on another team machine." },
  "message.federation_received": { icon: "&#9993;", label: "Message from another machine", cls: "tb-heartbeat", tip: "A message arrived from a chat on another team machine." },
  "message.federation_denied":   { icon: "&#8856;", label: "Message from another machine refused", cls: "tb-blocked", tip: "A message from another machine was refused." },
  "message.federation_store_failed": { icon: "&#9888;", label: "Message from another machine lost", cls: "tb-blocked", tip: "A message from another machine arrived but could not be saved." },
  "message.federation_orphan_reply": { icon: "&#9888;", label: "Reply to an unknown message", cls: "tb-expired", tip: "A reply from another machine pointed at a message this machine does not have." },
  "message.federation_nonstandard_id": { icon: "&#9888;", label: "Odd message id", cls: "tb-expired", tip: "A message from another machine used an unexpected id format." },
  "message.shadow_mirror_failed": { icon: "&#9888;", label: "Message copy failed",       cls: "tb-expired", tip: "A message was sent, but its backup copy could not be written." },
  // Audit events: team administration.
  "team.auto_created_personal":  { icon: "&#9733;", label: "Personal team created",      cls: "tb-acquired", tip: "A personal team was created automatically on first sign-in." },
  "member.seniority_changed":    { icon: "&#8645;", label: "Seniority changed",          cls: "tb-heartbeat", tip: "A member's seniority, which sets whose edits win, was changed." },
  "member.title_changed":        { icon: "&#9998;", label: "Title changed",              cls: "tb-released", tip: "A member's job title was changed." },
  "invitation.resent":           { icon: "&#9993;", label: "Invitation resent",          cls: "tb-heartbeat", tip: "An email invitation was sent again." },
  "invitation.revoked":          { icon: "&#10007;", label: "Invitation withdrawn",      cls: "tb-expired", tip: "A pending email invitation was withdrawn." },
  "device_auth.approved.dedup":  { icon: "&#9989;", label: "Device re-authorized",       cls: "tb-acquired", tip: "An already-approved device signed in again." },
  "machine.renamed":             { icon: "&#9998;", label: "Machine renamed",            cls: "tb-released", tip: "A machine was given a new name." },
  "machine.revoked":             { icon: "&#10007;", label: "Machine removed",           cls: "tb-blocked", tip: "A machine's access to the team was removed." },
  "machine.bumped":              { icon: "&#8645;", label: "Machine bumped off its seat", cls: "tb-expired", tip: "A newer machine took this machine's seat on the plan." },
  "machine_settings.updated":    { icon: "&#9881;", label: "Machine settings changed",   cls: "tb-heartbeat", tip: "Settings for a machine were changed from the dashboard." },
  "machine_settings.effective_reported": { icon: "&#9881;", label: "Machine reported its settings", cls: "tb-released", tip: "A machine reported the settings it is running with." },
  "role.created":                { icon: "&#43;",   label: "Role created",               cls: "tb-acquired", tip: "A custom role was created." },
  "role.deleted":                { icon: "&#8722;", label: "Role deleted",               cls: "tb-blocked", tip: "A custom role was deleted." },
  "role.scopes_changed":         { icon: "&#9881;", label: "Role permissions changed",   cls: "tb-heartbeat", tip: "What a role is allowed to do was changed." },
  "bu.created":                  { icon: "&#43;",   label: "Workspace created",          cls: "tb-acquired", tip: "A workspace was created." },
  "bu.renamed":                  { icon: "&#9998;", label: "Workspace renamed",          cls: "tb-released", tip: "A workspace was renamed." },
  "bu.deleted":                  { icon: "&#10007;", label: "Workspace deleted",         cls: "tb-blocked", tip: "A workspace was deleted." },
  "bu_member.added":             { icon: "&#43;",   label: "Added to workspace",         cls: "tb-acquired", tip: "A member was added to a workspace." },
  "bu_member.removed":           { icon: "&#8722;", label: "Removed from workspace",     cls: "tb-blocked", tip: "A member was removed from a workspace." },
  "bu_member.role_changed":      { icon: "&#8645;", label: "Workspace role changed",     cls: "tb-heartbeat", tip: "A member's role in a workspace was changed." },
  "repo.assigned_to_bu":         { icon: "&#8645;", label: "Repo moved to workspace",    cls: "tb-heartbeat", tip: "A repo was assigned to a workspace." },
  "api-token.created":           { icon: "&#43;",   label: "API token created",          cls: "tb-heartbeat", tip: "A new API token was created." },
  "api-token.edited":            { icon: "&#9998;", label: "API token edited",           cls: "tb-heartbeat", tip: "An API token's name or permissions were changed." },
  "api-token.extended":          { icon: "&#9201;", label: "API token extended",         cls: "tb-heartbeat", tip: "An API token's expiry was pushed back." },
  "api-token.rolled":            { icon: "&#10227;", label: "API token replaced",        cls: "tb-heartbeat", tip: "An API token was swapped for a fresh one." },
  "api-token.revoked":           { icon: "&#10007;", label: "API token revoked",         cls: "tb-expired", tip: "An API token was switched off." },
  "rule.updated":                { icon: "&#9999;", label: "Rule changed",               cls: "tb-heartbeat", tip: "A rule was edited." },
  "rule.deleted":                { icon: "&#10007;", label: "Rule deleted",              cls: "tb-heartbeat", tip: "A rule was deleted." },
  "rules.template_installed":    { icon: "&#9999;", label: "Rule template added",        cls: "tb-heartbeat", tip: "A ready-made set of rules was added." },
  "policy.updated":              { icon: "&#9999;", label: "Rules updated",              cls: "tb-heartbeat", tip: "The team's rules were saved." },
  "priority.updated":            { icon: "&#9881;", label: "Priorities updated",         cls: "tb-heartbeat", tip: "The team's priority settings were saved." },
  "priority.rule_appended":      { icon: "&#9999;", label: "Priority rule added",        cls: "tb-heartbeat", tip: "A priority rule was added." },
  "priority.rule_updated":       { icon: "&#9999;", label: "Priority rule changed",      cls: "tb-heartbeat", tip: "A priority rule was edited." },
  "priority.rule_deleted":       { icon: "&#10007;", label: "Priority rule deleted",     cls: "tb-heartbeat", tip: "A priority rule was deleted." },
  "settings.reset":              { icon: "&#9881;", label: "Settings reset",             cls: "tb-heartbeat", tip: "Team settings were put back to their defaults." },
  "billing.plan_changed":        { icon: "&#9733;", label: "Plan changed",               cls: "tb-heartbeat", tip: "The team's plan was changed." },
  "billing.updated":             { icon: "&#9733;", label: "Billing updated",            cls: "tb-heartbeat", tip: "The team's billing details were updated." },
  "billing.payment_failed":      { icon: "&#9888;", label: "Payment failed",             cls: "tb-blocked", tip: "A payment did not go through." },
  "billing.cancel_scheduled":    { icon: "&#9201;", label: "Cancellation scheduled",     cls: "tb-expired", tip: "The plan is set to cancel at the end of the billing period." },
  "billing.resumed":             { icon: "&#10003;", label: "Plan resumed",              cls: "tb-acquired", tip: "A scheduled cancellation was undone." },
  "billing.trial_granted":       { icon: "&#9733;", label: "Trial started",              cls: "tb-acquired", tip: "A free trial started." },
  "billing.trial_ending_soon":   { icon: "&#9201;", label: "Trial ending soon",          cls: "tb-expired", tip: "The free trial ends soon." },
  "billing.trial_ended":         { icon: "&#9201;", label: "Trial ended",                cls: "tb-expired", tip: "The free trial ended." },
  "billing.trial_record_failed": { icon: "&#9888;", label: "Trial not recorded",         cls: "tb-blocked", tip: "A trial started but could not be saved on the team." },
  "billing.comp_granted":        { icon: "&#9733;", label: "Free plan granted",          cls: "tb-acquired", tip: "The team was given a plan at no charge." },
  "billing.comp_revoked":        { icon: "&#10007;", label: "Free plan removed",         cls: "tb-expired", tip: "A no-charge plan was taken away." },
  "billing.comp_update_skipped": { icon: "&#9888;", label: "Free plan change skipped",   cls: "tb-expired", tip: "A change to a no-charge plan was skipped." },
  "billing.override_event_limit_set": { icon: "&#9881;", label: "Event limit changed",   cls: "tb-heartbeat", tip: "A custom limit on stored events was set for the team." },
};

// HIDDEN_EVENT_TYPES are housekeeping rows kept off the Events list. A machine
// reporting its own settings used to write one on every report.
export const HIDDEN_EVENT_TYPES = new Set(["machine_settings.effective_reported"]);

export function isHiddenEvent(eventType) {
  return HIDDEN_EVENT_TYPES.has(eventType);
}

// plainEventLabel turns a raw type nobody has labelled yet into words:
// "machine_settings.effective_reported" -> "Machine settings: effective reported".
function plainEventLabel(eventType) {
  const parts = String(eventType).split(".").map((p) => p.replace(/[_-]+/g, " ").trim()).filter(Boolean);
  if (!parts.length) return "Unknown event";
  const head = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  return parts.length > 1 ? head + ": " + parts.slice(1).join(" ") : head;
}

// friendlyEvent returns the {icon, label, cls, tip} for an event
// type. Special-cases lease.expired with heartbeat_stale payload
// ("Chat went silent" wording is more accurate than "Lease timed
// out"). Unknown event types fall back to a generic "•".
export function friendlyEvent(eventType, payload) {
  if (
    eventType === "lease.expired" &&
    payload &&
    payload.reason === "heartbeat_stale"
  ) {
    return {
      icon: "&#9888;",
      label: "Chat went silent",
      cls: "tb-blocked",
      tip: "The holder stopped sending heartbeats. The sweeper reclaimed the lease.",
    };
  }
  // Older versions recorded every change to a file nobody held, mostly git
  // and test runs in a chat's own folder. Nothing clashed, so don't show
  // them as caught conflicts.
  if (isOldUnheldChange(eventType, payload)) {
    return {
      icon: "&bull;",
      label: eventType.startsWith("delete") ? "Deleted without a lease" : "Changed without a lease",
      cls: "tb-released",
      tip: "A file changed while no chat held it, often git or a test run. Nothing else was holding it, so nothing clashed.",
    };
  }
  if (EVENT_META[eventType]) return EVENT_META[eventType];
  return {
    icon: "&bull;",
    label: plainEventLabel(eventType),
    cls: "tb-unknown",
    tip: "Unrecognized event type: " + eventType,
  };
}

// eventCategory buckets an event type into one of the four filter
// categories the Events page exposes. Prefix-derived so new event
// types land in the right bucket automatically (no per-type chip to
// keep in sync), and anything unrecognized falls into "system" rather
// than vanishing from the filtered view. The vocabulary is large
// (80+ types: lease lifecycle + negotiation, membership/role/team,
// invites, billing, auth, machine/repo/bu, settings/rules) and grows
// every release — categorizing by prefix is the only thing that scales.
export function eventCategory(eventType) {
  const t = eventType || "";
  if (
    t.startsWith("lease.") ||
    t.startsWith("branch.") ||
    t.startsWith("commit.") ||
    t.startsWith("write.") ||
    t.startsWith("delete.")
  ) {
    return "coordination";
  }
  if (
    t.startsWith("member.") ||
    t.startsWith("team.") ||
    t.startsWith("user.") ||
    t.startsWith("machine.") ||
    t.startsWith("role.") ||
    t.startsWith("bu.") ||
    t.startsWith("bu_member.")
  ) {
    return "membership";
  }
  if (t.startsWith("invite") || t.startsWith("invitation")) {
    return "invites";
  }
  // settings.*, rule*, priority.*, policy.*, device_auth.*, billing.*,
  // invoice.*, auth.*, message.*, retention.*, install-token.*, and any
  // future/unknown type all bucket here so nothing disappears.
  return "system";
}

// FLAGGED_EVENT_TYPES — the events AgentCollision flags as PROBLEMS worth a
// human's attention (vs the routine lifecycle: acquire/release/heartbeat/joins).
// This is the single source of truth shared by the Protection page's flagged-
// event feed AND the pattern detector behind the suggested-actions strip. These
// are exactly the EVENT_META rows styled tb-blocked / tb-expired (the "bad"
// outcomes), all confirmed emitted server-side (branch_collision.go,
// preempt_wire.go, sweeper.go, hooks_git.go, the unmanaged-write watcher).
export const FLAGGED_EVENT_TYPES = new Set([
  "branch.contended",
  "lease.blocked",
  "lease.preempted",
  "lease.revoked",
  "lease.expired",
  "write.unmanaged_conflict",
  "delete.unmanaged_conflict",
  "commit.bypass_detected",
]);

// realConflictsSince is when this computer started recording only real
// unheld changes (several chats in one folder); /v1/value reports it. 0 =
// unknown, e.g. on the team view.
let realConflictsSince = 0;

export function setRealConflictsSince(iso) {
  const t = Date.parse(iso || "");
  realConflictsSince = Number.isFinite(t) ? t : 0;
}

// isOldUnheldChange: a row older versions recorded for a change to a file
// nobody held (git and test runs in a chat's own folder). Not a conflict.
// Rows tagged "no_lease_in_managed_repo" after realConflictsSince are real
// (the watcher kept that tag for shared-folder changes until it gained
// "shared_folder"); payload._at is the row's time, set by decryptEventRow.
export function isOldUnheldChange(eventType, payload) {
  if (eventType !== "write.unmanaged_conflict" && eventType !== "delete.unmanaged_conflict") return false;
  if (!payload || payload.heuristic !== "no_lease_in_managed_repo") return false;
  return !(realConflictsSince && payload._at >= realConflictsSince);
}

// isFlaggedEvent reports whether an event is one AgentCollision flags as a
// problem (a collision, a forced release, an out-of-band write, an agent dying
// mid-edit). Used to filter the Protection feed and drive recommendations.
export function isFlaggedEvent(eventType, payload) {
  return FLAGGED_EVENT_TYPES.has(eventType || "") && !isOldUnheldChange(eventType, payload);
}

// ---- event groups: a small, legible taxonomy for color + icon ----
//
// Rather than 40 one-off colors, every event type rolls up into ONE of 8
// semantic GROUPS. Each group has a distinct color (a CSS var, so it themes)
// and a representative icon. This drives the row badge/dot AND the type-filter
// dropdown's optgroups, so the whole surface reads consistently: a red dot is
// always a collision, violet is always a forced takeover, etc. New event types
// inherit their group by prefix (groupForType), so nothing falls through
// uncolored as the vocabulary grows.
//
// The 8 groups (key → color token / icon / label):
export const EVENT_GROUPS = {
  collision:   { label: "Collision",   color: "var(--ev-collision)",   icon: "&#8856;" }, // ⊘ blocked/unmanaged write
  contention:  { label: "Contention",  color: "var(--ev-contention)",  icon: "&#9094;" }, // ⎆ branch/commit bypass
  preemption:  { label: "Preemption",  color: "var(--ev-preemption)",  icon: "&#8645;" }, // ⇅ forced takeover/revoke
  stale:       { label: "Stale",       color: "var(--ev-stale)",       icon: "&#9201;" }, // ⏱ timeout / went silent
  negotiation: { label: "Negotiation", color: "var(--ev-negotiation)", icon: "&#9993;" }, // ✉ autonomous handoff
  lifecycle:   { label: "Lifecycle",   color: "var(--ev-lifecycle)",   icon: "&#9654;" }, // ▶ healthy acquire/release
  membership:  { label: "Membership",  color: "var(--ev-membership)",  icon: "&#9823;" }, // ♟ member/team/invite/user
  system:      { label: "System",      color: "var(--ev-system)",      icon: "&#9881;" }, // ⚙ settings/rules/billing
};

// groupForType maps an event type to its group key. Exact matches first (so a
// healthy lease.released isn't lumped with a lease.blocked collision), then
// prefix fallbacks so unknown/future types still land somewhere sensible.
export function groupForType(eventType) {
  const t = eventType || "";
  // Collisions — an overwrite that was stopped or caught.
  if (
    t === "lease.blocked" ||
    t === "write.unmanaged_conflict" ||
    t === "delete.unmanaged_conflict" ||
    t === "delete.unmanaged_cascade" ||
    t === "write.unmanaged_cascade"
  )
    return "collision";
  // Contention — coordination conflicts beyond a single file lease.
  if (t === "branch.contended" || t.startsWith("commit.")) return "contention";
  // Preemption — a holder forced off (or asked to be).
  if (
    t === "lease.preempted" ||
    t === "lease.revoked" ||
    t === "lease.revoke_requested"
  )
    return "preemption";
  // Stale — a lease that timed out / the agent went silent.
  if (t === "lease.expired" || t === "lease.nudged") return "stale";
  // Negotiation — the autonomous-handoff protocol.
  if (t.startsWith("lease.negotiation_")) return "negotiation";
  // Healthy lifecycle — the routine, good-path lease events.
  if (t.startsWith("lease.")) return "lifecycle";
  // Membership — who's on the team.
  if (
    t.startsWith("member.") ||
    t.startsWith("team.") ||
    t.startsWith("user.") ||
    t.startsWith("invite") ||
    t.startsWith("invitation") ||
    t.startsWith("device_auth.") ||
    t.startsWith("install-token.") ||
    t.startsWith("machine.") ||
    t.startsWith("role.") ||
    t.startsWith("bu")
  )
    return "membership";
  // Everything else — settings, rules, policy, billing, retention, …
  return "system";
}

// eventGroup returns the full {key,label,color,icon} descriptor for an event
// type, so callers don't repeat the EVENT_GROUPS lookup.
export function eventGroup(eventType, payload) {
  const key = isOldUnheldChange(eventType, payload) ? "system" : groupForType(eventType);
  return { key, ...EVENT_GROUPS[key] };
}

// eventRowHTML formats a single event for the events list. Pure
// function: takes the event object + isNew flag, returns the
// innerHTML string for one <div class="event-row">.
//
// Handles both lease-coordination events (agent_id + resource +
// purpose payload, the high-frequency rows) and audit events
// (no agent_id, actor_id in payload, target/detail as strings).
// The two shapes share enough columns to render in one row class;
// the agent-context block shows tool/runtime for chat rows and a
// "by <actor>" string for audit rows.
// `idx` is the row's position in its list — used ONLY as a uniqueness
// tiebreaker in the expand key when an event has no real id (undecrypted cloud
// rows), so two events with identical type+agent+time+resource don't share a
// key and expand together. Callers pass their map index; it defaults to "" for
// single-row uses where collisions can't happen.
export function eventRowHTML(e, isNew, idx) {
  const eventType = e.EventType || e.event_type || "";
  const ts = e.OccurredAt || e.occurred_at || new Date().toISOString();
  const resource = e.Resource || e.resource || "";
  const payload = e.Payload || e.payload || {};
  const meta = friendlyEvent(eventType, payload);
  // Group descriptor → the badge's color. The icon + label stay per-type
  // (meta.*), but the COLOR is the group's, so one glance reads the kind of
  // event (red=collision, violet=preemption, …). Rendered via a `tb-group`
  // class that consumes the inline --ev custom property below.
  const grp = eventGroup(eventType, payload);

  // Warning pill: inline chip in the resource cell that flags
  // "this event leaves the file in a potentially-uncertain state —
  // check git status before trusting it." Three variants:
  //
  //   lease.expired heartbeat_stale → "stale heartbeat" — agent died
  //     mid-edit, strongest signal.
  //   lease.expired ttl_exceeded    → "lease timed out" — agent ran
  //     past TTL without releasing.
  //   lease.revoked                 → "force released" — admin forced
  //     release; may have interrupted an active edit.
  //
  // Pill replaces an earlier full-width banner (PR #168) — the row
  // is one line, the chip is visible without hover (PR #170), and the
  // tooltip carries the full explanation.
  let staleWarning = "";
  if (eventType === "lease.expired" && payload.reason === "heartbeat_stale") {
    staleWarning = `<span class="event-warning-pill" data-tooltip="${escapeHTML(payload.warning || "Chat stopped heartbeating mid-edit. Check git status on this file.")}"><span class="ic">&#9888;</span>stale heartbeat</span>`;
  } else if (eventType === "lease.expired") {
    staleWarning = `<span class="event-warning-pill" data-tooltip="${escapeHTML("Lease expired without an explicit release. The agent's last edits may not be committed — check git status.")}"><span class="ic">&#9888;</span>lease timed out</span>`;
  } else if (eventType === "lease.revoked") {
    const reason = typeof payload.reason === "string" ? payload.reason : "";
    const tip = reason
      ? `Force-released (${reason}). The previous holder may have been mid-edit — check git status.`
      : "Force-released. The previous holder may have been mid-edit — check git status.";
    staleWarning = `<span class="event-warning-pill" data-tooltip="${escapeHTML(tip)}"><span class="ic">&#9888;</span>force released</span>`;
  }

  const agentID = e.AgentID || e.agent_id || "";
  const realId = e.EventID || e.event_id || e.id || "";
  // The expand KEY must be unique per row. A real event id is used when present;
  // otherwise fall back to a deterministic content key (type + agent + time +
  // resource) so two rows with no id (e.g. undecrypted cloud rows) don't share
  // the empty string and expand together. The id CHIP is only shown for a real
  // id (the content key isn't a user-facing reference).
  const eid =
    realId !== ""
      ? String(realId)
      : "ev-" +
        (idx != null ? idx + "-" : "") +
        (e.EventType || e.event_type || "") +
        "-" +
        (agentID || "") +
        "-" +
        (e.OccurredAt || e.occurred_at || "") +
        "-" +
        (e.Resource || e.resource || "");
  const idChip = realId
    ? `<span class="event-id" data-tooltip="Event #${escapeHTML(String(realId))} — click to copy" data-copy="${escapeHTML(String(realId))}">#${escapeHTML(String(realId))}</span>`
    : "";

  // Audit-event rendering: no AgentID, but payload carries actor_id +
  // target. Show "by <actor>" in the agent-context column and the
  // detail text in the resource column. Distinguishes admin trail
  // rows from chat coordination rows.
  if (!agentID) {
    const actorRaw = payload.actor_id || "";
    // The unmanaged-write/delete watcher emits with no agent and no actor_id
    // (an *unmanaged* write has no coordinating agent to attribute it to), so
    // formatActor("") would fall back to the generic "system". Attribute these
    // to AgentCollision — its watcher is literally what caught them — while
    // leaving other empty-actor events (admin/system trail) as "system".
    const evType = e.EventType || e.event_type || "";
    const isWatcherEvent =
      !actorRaw &&
      (evType === "write.unmanaged_conflict" ||
        evType === "delete.unmanaged_conflict" ||
        evType === "delete.unmanaged_cascade" ||
        evType === "write.unmanaged_cascade");
    const actorLabel = isWatcherEvent ? "AgentCollision" : formatActor(actorRaw);
    // actorRaw is known-empty for watcher events, so give the agent-context
    // cell a meaningful hover instead of a blank tooltip.
    const actorTip = isWatcherEvent
      ? "Detected by the AgentCollision unmanaged-write watcher"
      : actorRaw;
    const cascadeCount = evType === "write.unmanaged_cascade" && payload.count ? payload.count + " files in " : "";
    const detail =
      payload.detail ||
      payload.target ||
      (resource.startsWith("repo:") ? cascadeCount + "repo " + resource.slice(5).replace(/\/+$/, "").split("/").pop() : "") ||
      (resource.startsWith("repo:") ? "repo " + resource.slice(5).replace(/\/+$/, "").split("/").pop() : friendlyResource(resource) || resource) ||
      "";
    const auditClick = eid
      ? ` event-row-clickable${eventIsExpanded(eid) ? " event-row-open" : ""}" data-event-expand-id="${escapeHTML(String(eid))}`
      : "";
    return `
      <div class="event-row${isNew ? " new-event" : ""}${auditClick}" data-tooltip="${realId ? "Event #" + escapeHTML(String(realId)) : "Click to expand"}">
        <span class="time">${escapeHTML(shortTime(ts))}</span>
        ${eventBadgeHTML(eventType, meta, grp)}
        <span class="agent-context" data-tooltip="${escapeHTML(actorTip)}">
          <span class="tool-label" style="color: var(--text-secondary);">by ${escapeHTML(actorLabel)}</span>
        </span>
        <span class="resource" data-tooltip="${escapeHTML(detail)}">${escapeHTML(detail)}</span>
        ${idChip}
        ${eid && eventIsExpanded(eid) ? eventDetailHTML(e) : ""}
      </div>`;
  }

  // Chat / coordination event rendering (the high-frequency path).
  const agent = parseAgentID(agentID);
  const isAC = isAgentCollisionID(agentID);
  const wt = worktreeFromPurpose(payload.purpose || payload.Purpose || "");
  const repo = repoFor(resource, payload.purpose || payload.Purpose || "");
  const agentTip =
    agentID +
    (repo ? "\nRepo: " + repo : "") +
    (wt ? "\nWorktree: " + wt : "");

  // Render the resource cell. The default path is
  // `<icon> friendlyResource(resource)` — but when friendlyResource
  // returns "" because the wire form is hashed and decryption isn't
  // working, we'd otherwise paint a blank cell with no indication of
  // why. Surface a clickable "needs decrypt" pill instead so users
  // can SEE which rows are blocked on a missing/wrong HMAC key (PR:
  // decrypt-failure-pill — sibling to PR #209 which fixed the boot
  // race but doesn't help when payload_hint is empty server-side or
  // the cached key is stale).
  const friendly = friendlyResource(resource);
  const hashedNoPlain = !friendly && looksLikeHashedResource(resource);
  // Two very different cases produce a hashed resource cell:
  //   - NO key loaded → the user CAN fix it (import the key). Show the
  //     actionable "needs decrypt key" link to the Machines page.
  //   - Key IS loaded but the row still has no plaintext (its payload_hint was
  //     empty server-side — common for older lease.expired/“went silent” cloud
  //     events) → NO key will ever recover this path. Showing "needs decrypt
  //     key" here is misleading (it sends the user to re-import a key they
  //     already have). Show a neutral, non-error label: "no path recorded".
  //     ("path unavailable" read like a failure; this event type simply never
  //     carried a path — e.g. a chat that went silent has no file to point at.)
  // The warning pill already says why a lease expired or was revoked.
  const extra = eventExtraDetail(staleWarning ? { ...payload, reason: "" } : payload, repo);
  // What the chat was doing, beside the file rather than in its place. The
  // cell ellipsizes, so it shows only as far as the row has room.
  const note = purposeNote(String(payload.purpose || payload.Purpose || ""));
  const extraHTML =
    (note ? ` <span class="event-extra event-purpose" data-tooltip="${escapeHTML(note)}">${escapeHTML(note)}</span>` : "") +
    (extra ? ` <span class="event-extra" data-tooltip="${escapeHTML(extra)}">${escapeHTML(extra)}</span>` : "");
  let resourceCell;
  if (hashedNoPlain && !state.hmacKey) {
    resourceCell = `<span class="resource" data-tooltip="This row's file path is scrambled with your team key, which isn't loaded in this browser yet. Click to load it.">${resourceTypeIcon(resource)} <a href="#" data-page-link="machines" style="color: var(--warning); text-decoration: none; font-weight: 600;">needs decrypt key</a>${staleWarning}${extraHTML}</span>`;
  } else if (hashedNoPlain) {
    resourceCell = `<span class="resource" data-tooltip="The team server has only a hashed form of this file's path: the event carried no readable path (AgentCollision before v0.76.4 sent none for most events). Your decryption key is loaded; there's nothing more to decrypt.">${resourceTypeIcon(resource)} <span style="color: var(--text-tertiary); font-style: italic;">file hidden</span>${staleWarning}${extraHTML}</span>`;
  } else {
    resourceCell = `<span class="resource" data-tooltip="${escapeHTML(resource)}">${resourceTypeIcon(resource)} ${escapeHTML(friendly)}${staleWarning}${extraHTML}</span>`;
  }
  const clickAttrs = eid
    ? ` event-row-clickable${eventIsExpanded(eid) ? " event-row-open" : ""}" data-event-expand-id="${escapeHTML(String(eid))}`
    : "";
  return `
      <div class="event-row${isNew ? " new-event" : ""}${clickAttrs}" data-tooltip="${realId ? "Event #" + escapeHTML(String(realId)) : "Click to expand"}">
        <span class="time">${escapeHTML(shortTime(ts))}</span>
        ${eventBadgeHTML(eventType, meta, grp)}
        <span class="agent-context" data-tooltip="${escapeHTML(agentTip)}">
          ${isAC ? "" : `<span>${toolIcon(agent.tool)}</span>`}
          <span class="tool-label">${escapeHTML(isAC ? chatLabel(agentID) : agent.tool)}</span>
          ${repo ? `<span class="wt-label" style="background: var(--info-soft, var(--bg-soft)); color: var(--info); font-weight: 600;" data-tooltip="repo: ${escapeHTML(repo)}">${escapeHTML(repo)}</span>` : ""}
          ${wt ? `<span class="wt-label">${escapeHTML(wt)}</span>` : isAC ? "" : `<span class="wt-label">${escapeHTML(agent.shortID)}</span>`}
        </span>
        ${resourceCell}
        ${idChip}
        ${eid && eventIsExpanded(eid) ? eventDetailHTML(e) : ""}
      </div>`;
}

// eventBadgeHTML is the type pill. The label can be long, so it ellipsizes;
// the tooltip carries the full label and the raw type.
function eventBadgeHTML(eventType, meta, grp) {
  const tip = meta.label + " (" + (eventType || "unknown") + ") — " + meta.tip;
  return `<span class="type-badge tb-group" style="--ev: ${grp.color};" data-group="${grp.key}" data-tooltip="${escapeHTML(tip)}">
          <span class="ic">${meta.icon}</span><span class="tb-label">${escapeHTML(meta.label)}</span>
        </span>`;
}

// eventExtraDetail is the short "branch · held by · reason · repo" line a row
// shows next to its file, so a row whose path can't be shown still says
// something. shownRepo is the repo already on the row, not repeated here.
function eventExtraDetail(payload, shownRepo) {
  const parts = [];
  const branch = payload.branch || payload.Branch;
  if (branch) parts.push("branch " + branch);
  const holder = payload.held_by_agent || payload.held_by || payload.holder_agent_id || payload.preempted_agent;
  if (holder) parts.push("held by " + chatLabel(String(holder)));
  const reason = typeof payload.reason === "string" ? payload.reason : "";
  if (reason) parts.push(reason.replace(/[_-]+/g, " "));
  const repoPath = payload.repo_root || payload.repo || "";
  const repoName = repoPath ? String(repoPath).replace(/\/+$/, "").split("/").pop() : "";
  if (repoName && repoName !== shownRepo) parts.push("repo " + repoName);
  return parts.join(" · ");
}

// formatActor turns a raw actor_id like "user:UUID" or "laptop_xxx"
// into a shorter, friendlier label. We don't have access to the
// emailMap / nameMap that the coordinator side builds (those are
// returned in the audit response separately) — the dashboard's
// fetch path could thread them through but for now we just trim
// the prefix and shorten the UUID. Better than rendering the raw
// "user:5720e2ad-50c1-4add-b2ad-f9b6268f8f9c" we shipped before.
function formatActor(actorID) {
  if (!actorID) return "system";
  if (actorID.startsWith("user:")) {
    const uuid = actorID.slice(5);
    return "user " + uuid.slice(0, 8);
  }
  if (actorID.startsWith("laptop_")) {
    return "laptop " + actorID.slice(7, 15);
  }
  return actorID;
}

// ---- chat label / activity ----

// truncFile truncates a filename to max chars, preserving the
// extension. "averyverylongname.ts" → "averyverylo….ts" when
// max=15. Returns input unchanged if already short enough.
export function truncFile(name, max) {
  if (!name || name.length <= max) return name;
  const ext = name.includes(".") ? "." + name.split(".").pop() : "";
  return name.slice(0, max - ext.length - 1) + "…" + ext;
}

// humanize converts identifiers to display-friendly strings:
//   "UPPER-CASE-WITH-DASHES" → "Upper Case With Dashes"
//   "camelCaseOrPascal"     → "Camel Case Or Pascal"
//   "snake_case_too"        → "Snake Case Too"
// Empty input returns "Unknown".
export function humanize(s) {
  if (!s) return "Unknown";
  if (s === s.toUpperCase() && s.includes("-")) {
    return s
      .split("-")
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(" ");
  }
  return (
    s
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim() || "Unknown"
  );
}

// inferActivity guesses what a chat is doing from the paths it
// touched. Returns {label, detail} where label is a humanized
// filename (e.g. "Login") and detail is a context string (e.g.
// "auth › services" or "3 files across 2 areas").
export function inferActivity(paths) {
  const cleaned = paths.map((p) => shortPath(p)).filter(Boolean);
  if (cleaned.length === 0) return { label: "Browsing", detail: "" };

  const fileInfos = cleaned.map((p) => {
    const parts = p.split("/");
    const fileName = parts[parts.length - 1] || "";
    const baseName = fileName.replace(/\.[^.]+$/, "");
    const generic = new Set([
      "src",
      "internal",
      "cmd",
      "pkg",
      "lib",
      "dist",
      "build",
      "app",
      "pages",
      "components",
      "utils",
    ]);
    const meaningful = parts
      .slice(0, -1)
      .filter((d) => !generic.has(d) && d.length > 1);
    return { baseName, meaningful, full: p, fileName };
  });

  const primary = fileInfos[0];
  const label = humanize(primary.baseName);

  const ctx = primary.meaningful;
  let detail = "";
  if (ctx.length > 0) {
    detail = ctx.slice(-2).map((s) => humanize(s)).join(" › ");
  }

  if (cleaned.length > 1) {
    const allMeaningful = fileInfos.flatMap((f) => f.meaningful);
    const dirCounts = {};
    for (const d of allMeaningful) dirCounts[d] = (dirCounts[d] || 0) + 1;
    const topDir = Object.entries(dirCounts).sort((a, b) => b[1] - a[1])[0];
    if (topDir && topDir[1] > 1) {
      detail =
        humanize(topDir[0]) + " — " + cleaned.length + " files";
    } else {
      const areaCount = new Set(
        fileInfos.map((f) => f.meaningful[0]).filter(Boolean),
      ).size;
      detail =
        areaCount > 0
          ? cleaned.length + " files across " + areaCount + " areas"
          : cleaned.length + " files";
    }
  }

  return { label, detail };
}

// inferChatLabel returns {name, detail} for a chat card given its
// agent_id, the most-recent file path, and the chat record itself.
// Falls back to "Browsing" when no decryptable paths are available
// (rather than dumping raw hex from the agent_id slice).
export function inferChatLabel(agentID, recentPath, chat) {
  if (!agentID) return { name: "unknown", detail: "" };

  const paths = [];
  if (chat && chat.files) {
    for (const [res] of chat.files) {
      if (res) paths.push(res);
    }
  }
  if (chat && chat.lastFile) paths.push(shortPath(chat.lastFile));
  if (recentPath) paths.push(shortPath(recentPath));
  const cleanedPaths = paths.filter(Boolean);

  if (cleanedPaths.length === 0) {
    return { name: "Browsing", detail: "" };
  }

  const activity = inferActivity(cleanedPaths);
  return { name: activity.label, detail: activity.detail };
}

// chatDescription builds the secondary line under a chat card's
// header: "Editing login.ts in auth — 5m ago" or "Idle for 12m" etc.
// Reads the chat record but doesn't mutate.
export function chatDescription(c, status, label, agent) {
  const timeAgo = ago(new Date(c.mostRecentTs).toISOString());
  // Bare duration for "idle for X" / "inactive since X" phrasings, where a
  // trailing " ago" from ago() would read redundantly ("idle for 5m ago").
  const dur = timeAgo.replace(/ ago$/, "");

  const recentLease =
    c.leases.length > 0
      ? c.leases
          .slice()
          .sort((a, b) => new Date(b.AcquiredAt) - new Date(a.AcquiredAt))[0]
      : null;
  const lastEditPath = recentLease ? shortPath(purposeToPath(recentLease.Purpose)) : "";
  const lastReadPath = c.lastFile ? shortPath(c.lastFile) : "";
  const lastEditFile = truncFile(
    lastEditPath ? lastEditPath.split("/").pop() : "",
    40,
  );
  const lastReadFile = truncFile(
    lastReadPath ? lastReadPath.split("/").pop() : "",
    40,
  );

  // Directory breadcrumb for the file currently in focus, e.g. "auth ›
  // services" from ".../auth/services/login.ts". Built directly from the path
  // (NOT from label.detail — inferActivity overloads detail with file-count
  // summaries like "4 files", which would read redundantly here). Drops the
  // filename and generic structural dirs (src/internal/cmd/…) so only the
  // meaningful subsystem segments remain; empty when there are none.
  const dirContext = (path) => {
    if (!path) return "";
    const generic = new Set([
      "src", "internal", "cmd", "pkg", "lib", "dist", "build",
      "app", "pages", "components", "utils",
    ]);
    const parts = path.split("/").slice(0, -1).filter((d) => !generic.has(d) && d.length > 1);
    if (parts.length === 0) return "";
    return " in " + parts.slice(-2).map((s) => humanize(s)).join(" › ");
  };

  // "(+N more files)" — how many OTHER files this chat touched this session,
  // beyond the one named in the line. c.files is the per-file touch map keyed
  // by the short/friendly path. We count keys whose filename differs from the
  // focus file's, rather than blindly `size - 1`: the focus file (esp. an edit
  // file derived from a lease purpose) may not be a key in c.files, in which
  // case `size - 1` would undercount. Matching on the basename is robust to the
  // edit-vs-read path-shape difference. Signals a chat ranging across a
  // subsystem vs. one camped on a single file.
  const moreFiles = (focusFile) => {
    if (!c || !c.files || c.files.size === 0) return "";
    const focusBase = focusFile ? focusFile.split("/").pop() : "";
    let extra = 0;
    for (const key of c.files.keys()) {
      if (key.split("/").pop() !== focusBase) extra++;
    }
    return extra > 0 ? ` (+${extra} more file${extra === 1 ? "" : "s"})` : "";
  };

  if (status === "active") {
    const count =
      c.totalLeases > 1 ? " — " + c.totalLeases + " files changed" : "";
    return "Editing " + (lastEditFile || "files") + dirContext(lastEditPath) + count;
  }
  if (status === "browsing") {
    if (lastReadFile)
      return (
        "Reading " +
        lastReadFile +
        dirContext(lastReadPath) +
        " via " +
        (c.lastTool || "Read") +
        moreFiles(lastReadFile) +
        " — " +
        timeAgo
      );
    // No read file to show. If track_reads is known-OFF, the chat IS doing
    // something (reading/searching) but the daemon isn't capturing which
    // files — so the card can only say "Browsing". Tell the user why and how
    // to fix it, instead of a silent generic label that reads like the chat
    // is doing nothing. Only when explicitly false (null = unknown → no nudge,
    // true → reads tracked but this chat genuinely has no file yet).
    if (state.trackReads === false) {
      return "Browsing — reads aren't tracked (enable Track Reads in Settings to see what this chat is doing) — " + timeAgo;
    }
    return "Browsing the codebase — " + timeAgo;
  }
  if (status === "idle") {
    if (lastEditFile)
      return "Last edited " + lastEditFile + dirContext(lastEditPath) + moreFiles(lastEditFile) + " — idle for " + dur;
    if (lastReadFile)
      return "Last read " + lastReadFile + dirContext(lastReadPath) + moreFiles(lastReadFile) + " — idle for " + dur;
    return "Idle for " + dur;
  }
  // inactive
  if (lastEditFile)
    return (
      "Was editing " + lastEditFile + dirContext(lastEditPath) + " — inactive since " + dur
    );
  if (lastReadFile)
    return (
      "Was reading " + lastReadFile + dirContext(lastReadPath) + " — inactive since " + dur
    );
  return "Inactive since " + dur;
}

// kindCountLabel renders a feed filter chip ("decisions (12)"). Until the
// source's first fetch finishes the count is unknown, so it reads
// "decisions (…)" rather than a misleading "(0)".
export function kindCountLabel(name, loaded, total) {
  return `${name} (${loaded ? total : "…"})`;
}
